import dynamic from "next/dynamic";
import { regions } from "@/data/regions";
import countriesJson from "@/data/countries.json";
import statesJSON from "@/data/states.json";
import { EXCLUDED_COUNTRIES } from "@/lib/constants";

/**
 * @param {string} countryCode - 2-letter country code
 * @returns {string | null}
 */
export const getRegionForCountry = (countryCode) => {
  if (!countryCode) return null;

  for (const region of regions) {
    if (region.countries.includes(countryCode.toUpperCase())) {
      return region.name;
    }
  }

  return null;
};

/**
 *
 * @param {string} countryCode - 2-letter country code
 * @returns {string | null}
 */
export const getCountryNameFromCode = (countryCode) => {
  if (!countryCode) return null;

  const foundCountry = countriesJson.find(
    (country) => country.isoCode === countryCode.toUpperCase()
  );
  if (foundCountry?.name) return foundCountry?.name;

  // Fallback to JS API if not found in JSON
  const regionNamesInEnglish = new Intl.DisplayNames(["en"], {
    type: "region",
  });
  return regionNamesInEnglish.of(countryCode.toUpperCase());
};

/**
 *
 * @param {string} countryName
 * @returns {string | null} countryCode - 2-letter country code
 */
export const getCountryCodeFromName = (
  countryName,
  { includedExcluded = true } = {}
) => {
  if (!countryName) return null;
  const foundCountry = countriesJson.find(
    (country) =>
      country.name.toLowerCase() === countryName.toLowerCase() &&
      (includedExcluded ? true : !EXCLUDED_COUNTRIES.includes(country.isoCode))
  );
  return foundCountry?.isoCode || null;
};

/**
 *
 * @param {string} countryCode - 2-letter country code
 * @param {string=} className
 * @returns
 */
export const getCountryLogo = (countryCode, className) => {
  const DynamicComponent = dynamic(() =>
    import("country-flag-icons/react/3x2").then((mod) => mod[countryCode])
  );
  return <DynamicComponent className={`h-full ${className}`} />;
};

/**
 * @param {string} countryCode - 2-letter country code
 * @returns {object[]}
 */
export const getStatesForCountryCode = (countryCode) => {
  if (!countryCode) return [];

  const countryStates = statesJSON[countryCode];
  if (!countryStates) return [];

  return countryStates;
};

/**
 * @returns {object[]} - Array of US military state objects { code, name }
 */
export const getMilitaryStatesForUS = () => {
  return [
    { code: "AA", name: "Armed Forces Americas" },
    { code: "AE", name: "Armed Forces Europe" },
    { code: "AP", name: "Armed Forces Pacific" },
  ];
};
